/**
 * useBarcodeScanner — detects fast keystroke bursts typical of USB/Bluetooth barcode/QR scanners.
 *
 * Real keyboards type 80-200ms between keys; barcode scanners blast a full code in 30-80ms total.
 * We buffer key events with low timestamps and fire onScan when Enter arrives (most scanners end with \n).
 *
 * Usage:
 *   useBarcodeScanner(onScan, { enabled: !typingInField });
 */
import { useEffect, useRef } from "react";

export default function useBarcodeScanner(onScan, { enabled = true, minLength = 4, maxKeyGapMs = 60 } = {}) {
  const bufferRef = useRef([]);
  const lastTimeRef = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    const handleKey = (e) => {
      // Skip when user is typing in an input/textarea (manual entry)
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      // Exception: if there's no editable focus, capture global scans
      if (editable) {
        // Still allow Enter handling on inputs marked data-scanner-input="true"
        if (!e.target?.dataset?.scannerInput) return;
      }

      const now = Date.now();
      const gap = now - lastTimeRef.current;
      lastTimeRef.current = now;

      if (e.key === "Enter") {
        // End of scan
        if (bufferRef.current.length >= minLength) {
          const code = bufferRef.current.join("");
          bufferRef.current = [];
          // Was the buffer assembled fast enough? Scanners are <50ms gaps on avg.
          // If gap from last key is > 200ms, treat as manual typing — but still fire.
          onScanRef.current?.(code);
          e.preventDefault();
        } else {
          bufferRef.current = [];
        }
        return;
      }

      // Ignore control keys
      if (e.key.length !== 1) return;

      // Reset buffer if too much time has passed since last key (manual typing)
      if (gap > 500) {
        bufferRef.current = [];
      }
      bufferRef.current.push(e.key);

      // Auto-fire on max length for scanners that don't append Enter
      if (bufferRef.current.length > 50) {
        bufferRef.current = [];
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [enabled, minLength, maxKeyGapMs]);
}
